import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { makeStyles, CssBaseline } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import {
  setLogoutReporting,
  setReporting,
} from "../redux/slices/reportingSlice";
import axios from "axios";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmationDialog from "./ConfirmationDialog";
import "./CommonDashboard.css";
import { Button } from "@mui/material";
import TableStatus from "./TableStatus";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDataGrid-row.Mui-selected": {
      backgroundColor: "#e0e0e0",
    },
    "& .MuiDataGrid-columnHeader": {
      backgroundColor: "#fafafa",
      width: "100%",
    },
    "& .MuiDataGrid-cell": {
      fontWeight: "normal",
      fontSize: "0.875rem",
      color: "rgba(0, 0, 0, 0.87)",
      width: "100%",
      letterSpacing: "0.01071em",
    },
    "& .MuiDataGrid-cell--textLeft": {
      textAlign: "left",
    },
    "& .MuiDataGrid-cell--textCenter": {
      textAlign: "center",
    },
    "& .MuiDataGrid-cell--textRight": {
      textAlign: "right",
    },
    "& .MuiDataGrid-columnsContainer": {
      backgroundColor: "#fafafa",
      width: "100%",
    },
    "& .MuiDataGrid-iconSeparator": {
      display: "none",
    },
  },
  table: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: "100%",
  },
}));

const ReportingTable = ({ selectedOption, errors, isLoading }) => {
  const [rows, setRows] = React.useState([]);
  const reporting = useSelector((state) => state.reporting);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [loading, setLoading] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const classes = useStyles();
  const dispatch = useDispatch();
  const api = localStorage.getItem("api");
  const emms = useSelector((state) => state.emms);
  const locationData = useSelector((state) => state.location);
  const headers = useSelector((state) => state.header);
  const emmsheaders = useSelector((state) => state.emmsheader);

  const parseCronString = (cronString) => {
    const [minute, hour, dayOfMonth, month, dayOfWeek] = cronString.split(" ");

    let scheduledDayOrDate = "";
    let scheduledTime = `${hour}:${minute}`;

    if (month === "*/2") {
      scheduledDayOrDate =
        dayOfMonth !== "*" ? `Day ${dayOfMonth}` : "Every Month";
    } else if (dayOfWeek === "*") {
      scheduledDayOrDate =
        dayOfMonth !== "*" ? `Day ${dayOfMonth}` : "Every Day";
    } else {
      const dayOfWeekMap = {
        0: "Sunday",
        1: "Monday",
        2: "Tuesday",
        3: "Wednesday",
        4: "Thursday",
        5: "Friday",
        6: "Saturday",
      };
      scheduledDayOrDate = dayOfWeekMap[dayOfWeek] || "Not Specified";
    }

    return { scheduledDayOrDate, scheduledTime };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const formattedRows = reporting.reporting.map((item) => {
          const { scheduledDayOrDate, scheduledTime } = parseCronString(
            item.cronString
          );
          return {
            id: item.id,
            mailID: item.mailId,
            dataRange: item.dataRange,
            scheduledDayOrDate,
            scheduledTime,
            lastReportSent: item.lastReportSent || "Not Sent",
          };
        });

        setRows(formattedRows);
      } catch (error) {
        console.error("Error fetching report data:", error);
      }
    };

    fetchData();
  }, [reporting.reporting, rows]);

  const columns = [
    { field: "id", headerName: "ID ", flex: 0.3 },
    { field: "mailID", headerName: "Email ", flex: 1 },
    {
      field: "dataRange",
      headerName: "Data Range",
      flex: 1,
    },
    {
      field: "scheduledDayOrDate",
      headerName: "Date/Day ",
      flex: 1,
    },
    {
      field: "scheduledTime",
      headerName: "Scheduled Time",
      flex: 1,
    },
    {
      field: "lastReportSent",
      headerName: "Last Report Sent",
      flex: 2,
    },
    {
      field: "delete",
      headerName: "Delete",
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="outlined"
          startIcon={<DeleteIcon />}
          color="error"
          onClick={() => handleDelete(params.row.id)}
          disabled={loading}
        >
          {loading ? "Deleting..." : "Delete"}
        </Button>
      ),
    },
  ];

  const handleDelete = (id) => {
    setDeleteItemId(id);
    setShowConfirmationDialog(true);
  };

  const handleConfirmDelete = () => {
    setShowConfirmationDialog(false);
    setLoading(true);
    const requestBody = {
      path: "reporting",
      query: { id: `${deleteItemId}` },
      emmsId: selectedOption,
      body: {},
      requestType: "DELETE",
      headers: emmsheaders?.emmsheaders?.headers,
    };

    axios
      .post(api + `/emms/piggyback`, requestBody, headers)
      .then(() => {
        setRows((prevRows) => {
          const updatedRows = prevRows?.filter(
            (row) => row.id !== deleteItemId
          );
          dispatch(setReporting(updatedRows));

          return updatedRows;
        });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCancelDelete = () => {
    setShowConfirmationDialog(false);
  };

 
  return (
    <>
      {rows?.length > 0 && errors == false ? (
        <div style={{ padding: "2.5% 10%", overflowX: "auto" }}>
          <CssBaseline />
          <DataGrid
            rows={rows}
            columns={columns}
            columnVisibilityModel={{
              id: false,
            }}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 5,
                },
              },
            }}
            pageSizeOptions={[5]}
            checkboxSelection
            disableRowSelectionOnClick
          />

          {showConfirmationDialog && (
            <ConfirmationDialog
              open={showConfirmationDialog}
              onClose={handleCancelDelete}
              onConfirm={handleConfirmDelete}
            />
          )}
        </div>
      ) : (
        <TableStatus loading={isLoading} errors={errors} />
      )}
    </>
  );
};

export default ReportingTable;
